import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Header, Icon, Label, Table } from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';

const localDate = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const BillingFXSettingV8 = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState([]);
  const [activeMode, setActiveMode] = useState('public');
  const [currentRate, setCurrentRate] = useState(null);
  const [form, setForm] = useState({
    rate_date: localDate(),
    quote_per_usd: '',
    source: '',
  });

  const loadRates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await API.get(
        '/api/billing/v8/admin/fx-rates?page=1&page_size=100'
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      setRates(response.data.data?.items || []);
      setActiveMode(response.data.data?.active_mode || 'public');
      setCurrentRate(response.data.data?.current || null);
    } catch (error) {
      showError(error.message || t('setting.billing.fx.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadRates().then();
  }, [loadRates]);

  const saveManual = async () => {
    if (!form.rate_date || !form.quote_per_usd.trim()) {
      showError(t('setting.billing.fx.errors.invalid'));
      return;
    }
    setLoading(true);
    try {
      const response = await API.post(
        '/api/billing/v8/admin/fx-rates/manual',
        form
      );
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      showSuccess(t('setting.billing.fx.messages.manual_created'));
      setForm((current) => ({ ...current, quote_per_usd: '', source: '' }));
      await loadRates();
    } catch (error) {
      showError(error.message || t('setting.billing.fx.errors.create'));
    } finally {
      setLoading(false);
    }
  };

  const refreshPublic = async () => {
    setLoading(true);
    try {
      const response = await API.post('/api/billing/v8/admin/fx-rates/refresh');
      if (!response) return;
      if (!response.data?.success) throw new Error(response.data?.message);
      showSuccess(
        response.data.data?.created
          ? t('setting.billing.fx.messages.public_created')
          : t('setting.billing.fx.messages.public_current')
      );
      await loadRates();
    } catch (error) {
      showError(error.message || t('setting.billing.fx.errors.refresh'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form loading={loading}>
      <Header as='h3'>{t('setting.billing.fx.title')}</Header>
      <Label color={activeMode === 'manual' ? 'orange' : 'green'}>
        {t(
          `setting.billing.fx.mode.${
            activeMode === 'manual' ? 'manual' : 'public'
          }`
        )}
      </Label>
      {currentRate && (
        <Label basic style={{ marginLeft: 8 }}>
          USD 1 = CNY {currentRate.quote_per_usd} ({currentRate.rate_date})
        </Label>
      )}

      <Form.Group widths='equal' style={{ marginTop: 16 }}>
        <Form.Input
          type='date'
          label={t('setting.billing.fx.fields.rate_date')}
          value={form.rate_date}
          onChange={(_, { value }) =>
            setForm((current) => ({ ...current, rate_date: value }))
          }
        />
        <Form.Input
          inputMode='decimal'
          label={t('setting.billing.fx.fields.quote_per_usd')}
          value={form.quote_per_usd}
          onChange={(_, { value }) =>
            setForm((current) => ({ ...current, quote_per_usd: value }))
          }
        />
        <Form.Input
          label={t('setting.billing.fx.fields.source')}
          value={form.source}
          maxLength={191}
          onChange={(_, { value }) =>
            setForm((current) => ({ ...current, source: value }))
          }
        />
      </Form.Group>
      <Button
        type='button'
        primary
        icon
        labelPosition='left'
        onClick={saveManual}
      >
        <Icon name='save' />
        {t('setting.billing.fx.actions.save_manual')}
      </Button>
      <Button type='button' icon labelPosition='left' onClick={refreshPublic}>
        <Icon name='refresh' />
        {t('setting.billing.fx.actions.refresh_public')}
      </Button>

      <div style={{ overflowX: 'auto', marginTop: 20 }}>
        <Table compact unstackable>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>
                {t('setting.billing.fx.fields.rate_date')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.fields.version')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.fx.fields.quote_per_usd')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.fx.fields.source_type')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.fx.fields.source')}
              </Table.HeaderCell>
              <Table.HeaderCell>
                {t('setting.billing.pricing.fields.effective_at')}
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {rates.map((rate) => (
              <Table.Row key={rate.id}>
                <Table.Cell>{rate.rate_date}</Table.Cell>
                <Table.Cell>{rate.version}</Table.Cell>
                <Table.Cell>{rate.quote_per_usd}</Table.Cell>
                <Table.Cell>
                  {t(
                    `setting.billing.fx.source_type.${
                      rate.source_type === 'manual' ? 'manual' : 'public'
                    }`
                  )}
                </Table.Cell>
                <Table.Cell>{rate.source}</Table.Cell>
                <Table.Cell>
                  {new Date(rate.effective_at * 1000).toLocaleString()}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </div>
    </Form>
  );
};

export default BillingFXSettingV8;
